import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';
import { baseSelectStyles } from '../../shared/selectStyles';


const AppointmentForm = ({ user, initialData = {}, onSave, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    patient_id: '',
    doctor_id: '',
    user_id: user?.id,
    scheduled_at: '',
    status: 'pending',
    notes: '',
    ...initialData,
  });

  useEffect(() => {
    if (initialData?.scheduled_at) {
      const utcDate = new Date(initialData.scheduled_at);
      const localDate = new Date(
        utcDate.getTime() - utcDate.getTimezoneOffset() * 60000
      );

      setFormData((prev) => ({
        ...prev,
        ...initialData,
        scheduled_at: localDate,
      }));
    }
  }, [initialData]);

  const patientOptions = patients.map((p) => ({ value: p.id, label: p.name }));
  const doctorOptions = doctors.map((d) => ({ value: d.id, label: d.name }));

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await fetch('/api/v1/users?role=doctor');
        const data = await res.json();
        setDoctors(data);
      } catch (err) {
        console.error('Failed to fetch doctors', err);
      }
    };

    const fetchPatients = async () => {
      try {
        const res = await fetch('/api/v1/patients');
        const data = await res.json();
        setPatients(data);
      } catch (err) {
        console.error('Failed to fetch patients', err);
      }
    };

    fetchDoctors();
    fetchPatients();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' })); // clear error
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.patient_id) newErrors.patient_id = 'Patient is required';
    if (!formData.doctor_id) newErrors.doctor_id = 'Doctor is required';
    if (!formData.scheduled_at) newErrors.scheduled_at = 'Date & time is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSave({
        ...formData,
        scheduled_at: formData.scheduled_at?.toISOString(),
      });
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'completed', label: 'Completed' },
  ];

  return (
    <div className="appointment-form-section">
      <form onSubmit={handleSubmit} className="appt-form space-y-4">
        {/* Patient */}
        <div>
          <label className="block font-medium">Patient:</label>
          <Select
            name="patient_id"
            options={patientOptions}
            value={patientOptions.find((opt) => opt.value === formData.patient_id) || null}
            onChange={(opt) => {
              setFormData((prev) => ({ ...prev, patient_id: opt?.value }));
              setErrors((prev) => ({ ...prev, patient_id: '' }));
            }}
            placeholder="Select patient..."
            isClearable
            styles={baseSelectStyles}
            error={!!errors.patient_id}
          />
          {errors.patient_id && (
            <p className="error-msg">{errors.patient_id}</p>
          )}
        </div>

        {/* Doctor */}
        <div>
          <label className="block font-medium">Doctor:</label>
          <Select
            name="doctor_id"
            options={doctorOptions}
            value={doctorOptions.find((opt) => opt.value === formData.doctor_id) || null}
            onChange={(opt) => {
              setFormData((prev) => ({ ...prev, doctor_id: opt?.value }));
              setErrors((prev) => ({ ...prev, doctor_id: '' }));
            }}
            placeholder="Select doctor..."
            isClearable
            styles={baseSelectStyles}
            error={!!errors.doctor_id}
          />
          {errors.doctor_id && (
            <p className="error-msg">{errors.doctor_id}</p>
          )}
        </div>

        {/* Date & Time */}
        <div>
          <label className="block font-medium">Date & Time:</label>
          <DatePicker
            selected={formData.scheduled_at}
            onChange={(date) => {
              setFormData((prev) => ({ ...prev, scheduled_at: date }));
              setErrors((prev) => ({ ...prev, scheduled_at: '' }));
            }}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="MMMM d, yyyy h:mm aa"
            placeholderText="Select date and time"
            className={`date w-full p-2 border rounded ${
              errors.scheduled_at ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.scheduled_at && (
            <p className="error-msg">{errors.scheduled_at}</p>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="block font-medium">Status:</label>
          <Select
            name="status"
            options={statusOptions}
            value={statusOptions.find((opt) => opt.value === formData.status)}
            onChange={(opt) => setFormData((prev) => ({ ...prev, status: opt?.value }))}
            styles={baseSelectStyles}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block font-medium">Notes:</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="w-full border border-gray-300 rounded p-2"
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-between mt-4">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="primary wide bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? initialData.id
                ? 'Updating…'
                : 'Creating…'
              : initialData.id
              ? 'Update Appointment'
              : 'Create Appointment'}
          </button>
        </div>
      </form>
    </div>
  );
};

AppointmentForm.propTypes = {
  user: PropTypes.object.isRequired,
  initialData: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default AppointmentForm;
